if (!customElements.get('ca-restock')) {
  customElements.define('ca-restock', class extends HTMLElement {
    connectedCallback() {
      const customPage = this.dataset.adapter === 'cwf';
      this.root = this.closest(customPage ? '[data-ca-cwf-production]' : 'product-info');
      this.dialog = this.querySelector('dialog');
      if (!this.root || !this.dialog?.showModal || (!customPage && !window.MinimogEvents)) return;
      this.form = this.querySelector('form');
      this.status = this.querySelector('[data-ca-restock-status]');
      this.continueButton = this.querySelector('[data-ca-restock-continue]');
      this.heading = this.dialog.querySelector('h2');
      this.intro = this.dialog.querySelector('.ca-restock__intro');
      this.formTitle = this.heading.textContent;
      this.formIntro = this.intro.textContent;
      this.successIcon = this.querySelector('[data-ca-restock-success-icon]');
      this.footer = this.dialog.querySelector('.ca-restock__footer');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitText = this.submitButton.textContent;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ca-restock-open]');
        if (button && this.root.contains(button) && this.variant) {
          event.preventDefault();
          this.opener = button;
          this.open();
        }
      }, options);
      this.querySelector('[data-ca-restock-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => { this.hidden = !this.variant; this.opener?.focus(); }, options);
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.submit(); }, options);
      this.continueButton.addEventListener('click', () => this.dialog.close(), options);
      if (customPage) {
        this.root.addEventListener('ca:cwf:variant-change', (event) => {
          this.setVariant(this.readCustomVariant(event.detail?.variantId));
          if (!this.busy) this.dialog.close();
        }, options);
        this.setVariant(this.readCustomVariant(this.root.querySelector('[data-ca-production-variant-input]')?.value));
        return;
      }
      this.unsubscribers = [
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.optionValueSelectionChange, ({ data }) => {
          if (this.root.contains(data.event?.target)) {
            this.setVariant(null);
            if (!this.busy) this.dialog.close();
          }
        }),
        MinimogEvents.subscribe(MinimogTheme.pubSubEvents.variantChange, ({ data }) => {
          if (String(data.sectionId) !== this.dataset.sectionId) return;
          const fresh = data.html?.querySelector('ca-restock');
          this.setVariant(fresh && String(data.variant?.id) === fresh.dataset.variantId ? this.readVariant(fresh) : null);
        })
      ];
      const needsChoice = this.root.dataset.disableSelectedVariantDefault === 'true' && !new URL(location.href).searchParams.has('variant');
      this.setVariant(needsChoice ? null : this.readVariant(this));
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe?.());
      this.dialog?.close();
      this.setVariant(null);
    }

    readVariant(element) {
      const data = element.dataset;
      if (data.eligible !== 'true' || !/^\d+$/.test(data.variantId)) return null;
      return { id: data.variantId, title: data.variantTitle, product: data.productTitle || this.dataset.productTitle, image: data.image };
    }

    readCustomVariant(id) {
      const source = this.querySelector('template[data-ca-restock-variants]');
      const element = Array.from(source?.content.querySelectorAll('[data-variant-id]') || []).find((item) => item.dataset.variantId === String(id));
      return element ? this.readVariant(element) : null;
    }

    setVariant(variant) {
      this.variant = variant;
      this.hidden = !variant && !this.dialog?.open;
      this.root?.querySelector('.main-product__block-buy_buttons')?.toggleAttribute('data-ca-restock-active', !!variant);
      this.root?.querySelector('sticky-atc')?.toggleAttribute('data-ca-restock-active', !!variant);
      if (this.dataset.adapter === 'cwf') this.root?.toggleAttribute('data-ca-restock-active', !!variant);
      this.root?.querySelectorAll('[data-ca-restock-open]').forEach((button) => { button.hidden = !variant; });
      if (variant) this.querySelector('.ca-restock__help a').href = this.helpUrl(variant);
    }

    helpUrl(variant) {
      const productLink = new URL(location.href);
      productLink.search = '';
      productLink.hash = '';
      productLink.searchParams.set('variant', variant.id);
      return `mailto:info@ceramicaura.com?subject=${encodeURIComponent(`${variant.product} — ${variant.title}`)}&body=${encodeURIComponent(productLink.href)}`;
    }

    open() {
      if (this.dialog.open || this.busy) return;
      this.requested = { ...this.variant };
      this.dialog.classList.remove('is-success');
      this.heading.textContent = this.formTitle;
      this.intro.textContent = this.formIntro;
      this.successIcon.hidden = true;
      this.footer.hidden = false;
      this.form.hidden = false;
      this.continueButton.hidden = true;
      this.status.textContent = '';
      this.querySelector('[data-ca-restock-product]').textContent = this.requested.product.split(' | ')[0];
      this.querySelector('[data-ca-restock-product]').title = this.requested.product;
      this.querySelector('[data-ca-restock-variant]').textContent = this.requested.title === 'Default Title' ? '' : this.requested.title;
      const image = this.querySelector('[data-ca-restock-image]');
      image.hidden = !this.requested.image;
      if (this.requested.image) image.src = this.requested.image;
      this.dialog.querySelector('[data-ca-restock-help]').href = this.helpUrl(this.requested);
      this.dialog.showModal();
      this.form.elements.email.focus();
    }

    async request(action, payload) {
      const root = this.dataset.localeRoot.replace(/\/$/, '');
      const path = this.dataset.proxyPath;
      if (!/^\/apps\/[a-z0-9-]+$/.test(path)) throw new Error('invalid_endpoint');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(`${root}${path}/v1/${action}`, {
          method: 'POST', headers: { 'content-type': 'application/json' },
          credentials: 'same-origin', cache: 'no-store', body: JSON.stringify(payload), signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok || result.error) throw new Error(result.error || 'request_failed');
        return result;
      } finally { clearTimeout(timer); }
    }

    async submit() {
      if (this.busy || !this.requested || !this.form.reportValidity()) return;
      this.busy = true;
      this.submitButton.disabled = true;
      this.submitButton.textContent = this.dataset.submitting;
      this.status.textContent = '';
      try {
        const result = await this.request('subscriptions', {
          email: this.form.elements.email.value.trim(), firstName: this.form.elements.firstName.value.trim(), variantId: this.requested.id,
          locale: this.dataset.locale.split('-')[0], localeRoot: `${this.dataset.localeRoot.replace(/\/$/, '')}/`
        });
        if (result.status !== 'registered') throw new Error('invalid_response');
        this.form.hidden = true;
        this.footer.hidden = true;
        this.dialog.classList.add('is-success');
        this.successIcon.hidden = false;
        this.heading.textContent = this.dataset.successTitle;
        this.intro.textContent = this.dataset.successBody;
        this.continueButton.hidden = false;
        this.status.textContent = '';
        this.heading.focus();
      } catch (error) {
        this.status.textContent = error.message === 'already_available' ? this.dataset.available : error.message === 'too_many_requests' ? this.dataset.rateLimit : this.dataset.error;
      } finally {
        this.busy = false;
        this.submitButton.disabled = false;
        this.submitButton.textContent = this.submitText;
      }
    }

  });
}
